import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { validatePhone, getCountryFlag, handlePhoneChangeHelper } from '../../../utils/phoneValidation';
import './BookingPopup.css';

function BookingPopup() {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  // Handle scroll event - show popup on first scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!hasScrolled) {
        setHasScrolled(true);
        setIsVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasScrolled]);

  // After first scroll, show popup every 15 seconds
  useEffect(() => {
    if (hasScrolled) {
      const popupInterval = setInterval(() => {
        setIsVisible(true);
      }, 15000);

      return () => clearInterval(popupInterval);
    }
  }, [hasScrolled]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handlePhoneChange = useCallback((e) => {
    const value = handlePhoneChangeHelper(e.target.value);
    setPhoneNumber(value);
    if (phoneError) {
      setPhoneError('');
    }
  }, [phoneError]);

  const handlePhoneBlur = useCallback(() => {
    const error = validatePhone(phoneNumber, t);
    setPhoneError(error);
  }, [phoneNumber, t]);

  const handlePhoneFocus = useCallback(() => {
    if (!phoneNumber) {
      setPhoneNumber('+');
    }
  }, [phoneNumber]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneValidationError = validatePhone(phoneNumber, t);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      return;
    }

    setIsSubmitting(true);

    try {
      const body = {
        name: name || 'Pop-up dan',
        phoneNumber: phoneNumber,
        comment: 'Pop-up dan telefon raqam',
        lang: i18n.language || 'uz',
        site: 'rever'
      };

      const response = await axios.post('/v1/api/request', body);
      
      if (response.data) {
        setPhoneNumber('');
        setName('');
        setSubmitStatus('success');
        
        // Auto close popup after 3 seconds
        setTimeout(() => {
          setIsVisible(false);
          setSubmitStatus(null);
        }, 3000);
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    setSubmitStatus(null);
  };

  if (!isVisible) return null;

  return (
    <div className="booking-popup-overlay">
      <div className="booking-popup-container">
        <button className="booking-popup-close" onClick={handleClose}>
          &times;
        </button>

        {!submitStatus && (
          <div className="booking-popup-content">
            <h2>{t('popup.title') || 'Bron qiling'}</h2>
            <p>{t('popup.description') || 'Ma\'lumotlaringizni qoldiring, biz siz bilan bog\'lanamiz'}</p>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>{t('popup.name') || 'Ismingiz'}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('popup.namePlaceholder') || 'Ismingizni kiriting'}
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label>{t('popup.phone') || 'Telefon raqam'}</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    onFocus={handlePhoneFocus}
                    placeholder="+998 90 123 45 67"
                    maxLength={20}
                    className={phoneError ? 'error' : ''}
                    style={{ paddingRight: '45px' }}
                    disabled={isSubmitting}
                  />
                  {phoneNumber && phoneNumber.length > 1 && (
                    <span className="country-flag">
                      {getCountryFlag(phoneNumber)}
                    </span>
                  )}
                </div>
                {phoneError && (
                  <span className="error-message">{phoneError}</span>
                )}
              </div>

              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? (t('popup.sending') || 'Yuborilmoqda...') : (t('popup.submit') || 'Yuborish')}
              </button>
            </form>
          </div>
        )}

        {submitStatus === 'success' && (
          <div className="popup-status success">
            <div className="status-icon">
              <i className="bx bx-check-circle"></i>
            </div>
            <h3>{t('popup.success') || 'Muvaffaqiyatli!'}</h3>
            <p>{t('popup.successText') || 'Tez orada siz bilan bog\'lanamiz'}</p>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="popup-status error">
            <div className="status-icon">
              <i className="bx bx-x-circle"></i>
            </div>
            <h3>{t('popup.error') || 'Xatolik!'}</h3>
            <p>{t('popup.errorText') || 'Qayta urinib ko\'ring'}</p>
            <button className="retry-button" onClick={handleRetry}>
              {t('popup.retry') || 'Qayta urinish'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default BookingPopup;
